import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Plus,
  Refrigerator,
  Snowflake,
  Package,
  Leaf,
  AlertTriangle,
  Trash2,
  Edit2,
  Clock,
  Sparkles,
  Loader2,
  ScanBarcode,
  ChevronRight,
  Search,
  X,
  Calendar,
  Check
} from 'lucide-react';
import { haptic } from '@/utils/haptics';

// Categories for pantry items
const CATEGORIES = [
  { value: 'fridge', label: 'Fridge', icon: Refrigerator, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/30' },
  { value: 'freezer', label: 'Freezer', icon: Snowflake, color: 'text-cyan-500', bg: 'bg-cyan-50 dark:bg-cyan-950/30' },
  { value: 'pantry', label: 'Pantry', icon: Package, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/30' },
  { value: 'spices', label: 'Spices', icon: Leaf, color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-950/30' },
];

/**
 * Mobile-optimized Pantry Component
 * Goal: Quick inventory management with expiring items front and center
 */
const MobilePantry = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  // Data state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiringItems, setExpiringItems] = useState([]);
  const [useItUpRecipes, setUseItUpRecipes] = useState([]);
  
  // Filter state
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sheet states
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    quantity: 1,
    unit: '',
    category: 'fridge',
    expires_at: ''
  });

  // Load pantry data
  const loadPantry = useCallback(async () => {
    try {
      const [pantryRes, expiringRes, recipesRes] = await Promise.all([
        api.get('/pantry'),
        api.get('/pantry/expiring?days=7'),
        api.get('/pantry/use-it-up').catch(() => ({ data: [] }))
      ]);
      
      setItems(pantryRes.data || []);
      setExpiringItems(expiringRes.data || []);
      setUseItUpRecipes(recipesRes.data || []);
    } catch (error) {
      console.error('Failed to load pantry:', error);
      toast.error('Failed to load pantry');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadPantry();
  }, [loadPantry]);

  // Filter items
  const filteredItems = items.filter(item => {
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    const matchesSearch = !searchQuery || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Group items by category
  const groupedItems = CATEGORIES.map(cat => ({
    ...cat,
    items: filteredItems.filter(item => item.category === cat.value)
  })).filter(group => group.items.length > 0);

  // Calculate days until expiry
  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Handle add/edit item
  const handleSaveItem = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter an item name');
      return;
    }

    setSaving(true);
    haptic('medium');

    try {
      if (editingItem) {
        await api.put(`/pantry/${editingItem.id}`, formData);
        toast.success('Item updated!');
      } else {
        await api.post('/pantry', formData);
        toast.success('Item added to pantry!');
        haptic('success');
      }
      
      await loadPantry();
      handleCloseSheet();
    } catch (error) {
      console.error('Failed to save item:', error);
      toast.error('Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete item
  const handleDeleteItem = async (itemId) => {
    haptic('medium');
    try {
      await api.delete(`/pantry/${itemId}`);
      setItems(items.filter(i => i.id !== itemId));
      toast.success('Item removed');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Open add sheet
  const handleOpenAdd = () => {
    haptic('light');
    setEditingItem(null);
    setFormData({
      name: '',
      quantity: 1,
      unit: '',
      category: 'fridge',
      expires_at: ''
    });
    setAddSheetOpen(true);
  };

  // Open edit sheet
  const handleOpenEdit = (item) => {
    haptic('light');
    setEditingItem(item);
    setFormData({
      name: item.name,
      quantity: item.quantity || 1,
      unit: item.unit || '',
      category: item.category || 'fridge',
      expires_at: item.expires_at ? item.expires_at.split('T')[0] : ''
    });
    setAddSheetOpen(true);
  };

  // Close sheet
  const handleCloseSheet = () => {
    setAddSheetOpen(false);
    setEditingItem(null);
    setFormData({
      name: '',
      quantity: 1,
      unit: '',
      category: 'fridge',
      expires_at: ''
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="mobile-pantry">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-semibold">My Pantry</h1>
            <p className="text-sm text-muted-foreground">
              {items.length} item{items.length !== 1 ? 's' : ''} tracked
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleOpenAdd}
            data-testid="add-item-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search pantry..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-10 rounded-full bg-muted/50"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
          <Button
            variant={activeCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            className="flex-shrink-0 h-8 rounded-full"
            onClick={() => { haptic('light'); setActiveCategory('all'); }}
          >
            All ({items.length})
          </Button>
          {CATEGORIES.map(cat => {
            const count = items.filter(i => i.category === cat.value).length;
            const Icon = cat.icon;
            return (
              <Button
                key={cat.value}
                variant={activeCategory === cat.value ? 'default' : 'outline'}
                size="sm"
                className="flex-shrink-0 h-8 rounded-full"
                onClick={() => { haptic('light'); setActiveCategory(cat.value); }}
              >
                <Icon className={`h-3.5 w-3.5 mr-1 ${activeCategory !== cat.value ? cat.color : ''}`} />
                {cat.label} ({count})
              </Button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4 space-y-6">
        {/* Expiring Items Alert */}
        {expiringItems.length > 0 && (
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <span className="font-medium text-amber-700 dark:text-amber-400">
                  {expiringItems.length} item{expiringItems.length > 1 ? 's' : ''} expiring soon
                </span>
              </div>
              <div className="space-y-2">
                {expiringItems.slice(0, 3).map(item => {
                  const days = getDaysUntilExpiry(item.expires_at);
                  return (
                    <div 
                      key={item.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span>{item.name}</span>
                      <Badge 
                        variant={days <= 2 ? 'destructive' : 'secondary'}
                        className="text-xs"
                      >
                        {days <= 0 ? 'Expired!' : `${days} day${days > 1 ? 's' : ''}`}
                      </Badge>
                    </div>
                  );
                })}
              </div>
              {useItUpRecipes.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-3 border-amber-300 dark:border-amber-700"
                  onClick={() => navigate(`/recipes?query=${encodeURIComponent(expiringItems[0]?.name || '')}`)}
                >
                  <Sparkles className="h-4 w-4 mr-2 text-amber-500" />
                  Find recipes to use them up
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Use It Up Recipes */}
        {useItUpRecipes.length > 0 && (
          <div>
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Use It Up Recipes
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {useItUpRecipes.slice(0, 5).map((recipe, idx) => (
                <Card 
                  key={idx}
                  className="flex-shrink-0 w-40 cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => navigate(`/recipes/${recipe.id}`)}
                >
                  <div className="aspect-[4/3] overflow-hidden rounded-t-lg">
                    <img 
                      src={recipe.image} 
                      alt={recipe.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-2">
                    <p className="text-xs font-medium line-clamp-2">{recipe.title}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Pantry Items by Category */}
        {filteredItems.length > 0 ? (
          <div className="space-y-4">
            {activeCategory === 'all' ? (
              // Show grouped by category
              groupedItems.map(group => {
                const Icon = group.icon;
                return (
                  <div key={group.value}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`h-4 w-4 ${group.color}`} />
                      <span className="text-sm font-medium">{group.label}</span>
                      <Badge variant="secondary" className="text-xs">
                        {group.items.length}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {group.items.map(item => (
                        <PantryItemCard
                          key={item.id}
                          item={item}
                          onEdit={() => handleOpenEdit(item)}
                          onDelete={() => handleDeleteItem(item.id)}
                          getDaysUntilExpiry={getDaysUntilExpiry}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              // Show filtered list
              <div className="space-y-2">
                {filteredItems.map(item => (
                  <PantryItemCard
                    key={item.id}
                    item={item}
                    onEdit={() => handleOpenEdit(item)}
                    onDelete={() => handleDeleteItem(item.id)}
                    getDaysUntilExpiry={getDaysUntilExpiry}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          // Empty state
          <div className="text-center py-12" data-testid="empty-pantry">
            <Package className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchQuery ? 'No items found' : 'Your pantry is empty'}
            </h3>
            <p className="text-muted-foreground mb-6 text-sm">
              {searchQuery 
                ? 'Try a different search term'
                : 'Add items to track what you have and get expiry alerts'
              }
            </p>
            {!searchQuery && (
              <Button onClick={handleOpenAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Item
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Sheet */}
      <Sheet open={addSheetOpen} onOpenChange={setAddSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-8">
          <SheetHeader className="text-left pb-4">
            <SheetTitle>
              {editingItem ? 'Edit Item' : 'Add to Pantry'}
            </SheetTitle>
            <SheetDescription>
              {editingItem ? 'Update item details' : 'Track what you have at home'}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Item Name</label>
              <Input
                placeholder="e.g., Milk, Eggs, Chicken..."
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="item-name-input"
              />
            </div>

            {/* Quantity & Unit */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Quantity</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Unit (optional)</label>
                <Input
                  placeholder="lbs, oz, cups..."
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Storage Location</label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => {
                    const Icon = cat.icon;
                    return (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${cat.color}`} />
                          {cat.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Expiry Date */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Expires (optional)</label>
              <Input
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              {editingItem && (
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => {
                    handleDeleteItem(editingItem.id);
                    handleCloseSheet();
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
              <Button
                className="flex-1"
                onClick={handleSaveItem}
                disabled={saving || !formData.name.trim()}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {editingItem ? 'Update' : 'Add Item'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

// Pantry Item Card Component
const PantryItemCard = ({ item, onEdit, onDelete, getDaysUntilExpiry }) => {
  const days = getDaysUntilExpiry(item.expires_at);
  const isExpiring = days !== null && days <= 7;
  const isExpired = days !== null && days <= 0;

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-xl bg-card border transition-all active:scale-[0.99] ${
        isExpired ? 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20' :
        isExpiring ? 'border-amber-200 dark:border-amber-800' : 'border-border/50'
      }`}
      onClick={onEdit}
      data-testid={`pantry-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Item info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{item.name}</span>
          {item.quantity > 1 && (
            <Badge variant="secondary" className="text-xs">
              ×{item.quantity} {item.unit || ''}
            </Badge>
          )}
        </div>
        {days !== null && (
          <div className="flex items-center gap-1 mt-1">
            <Clock className={`h-3 w-3 ${isExpired ? 'text-red-500' : isExpiring ? 'text-amber-500' : 'text-muted-foreground'}`} />
            <span className={`text-xs ${isExpired ? 'text-red-500' : isExpiring ? 'text-amber-500' : 'text-muted-foreground'}`}>
              {isExpired ? 'Expired!' : `${days} day${days > 1 ? 's' : ''} left`}
            </span>
          </div>
        )}
      </div>

      {/* Edit indicator */}
      <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
    </div>
  );
};

export default MobilePantry;
